/**
 * Leviathan Web - Monthly Revenue Widget
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows current month revenue with MoM and YoY comparisons
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { DollarSign, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { API_URL } from '@/config/api';

interface RevenueSummary {
  currentMonth: { revenue: number; invoiceCount: number };
  lastMonth: { revenue: number; invoiceCount: number };
  lastYear: { revenue: number; invoiceCount: number };
  momChange: number;
  yoyChange: number;
}

const MonthlyRevenueWidget = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState<RevenueSummary | null>(null);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/revenue-summary`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          setData(await response.json());
        }
      } catch {
        // silent
      }
    };
    fetchRevenue();
  }, [accessToken]);

  if (!data) return null;

  const formatCurrency = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
  const formatFull = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const TrendIndicator = ({ value }: { value: number }) => {
    if (value > 0) return <TrendingUp className="w-3 h-3 text-success-green" />;
    if (value < 0) return <TrendingDown className="w-3 h-3 text-error-red" />;
    return <Minus className="w-3 h-3 text-text-tertiary" />;
  };

  const trendColor = (value: number) =>
    value > 0 ? 'text-success-green' : value < 0 ? 'text-error-red' : 'text-text-tertiary';

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center space-x-2 mb-3">
        <DollarSign className="w-4 h-4 text-amber" />
        <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
          Monthly Revenue
        </span>
      </div>

      {/* Current Month */}
      <div
        className="text-3xl font-mono font-bold text-amber mb-1"
        style={{ textShadow: '0 0 15px rgba(255,149,0,0.6)' }}
        title={formatFull(data.currentMonth.revenue)}
      >
        {formatCurrency(data.currentMonth.revenue)}
      </div>
      <div className="text-text-tertiary text-xs font-mono mb-4">
        {data.currentMonth.invoiceCount} invoices this month
      </div>

      {/* Comparisons */}
      <div className="space-y-2 mt-auto">
        <div className="flex items-center justify-between">
          <span className="text-text-tertiary text-xs font-mono">vs Last Month</span>
          <div className="flex items-center space-x-1">
            <TrendIndicator value={data.momChange} />
            <span className={`text-xs font-mono font-bold ${trendColor(data.momChange)}`}>
              {data.momChange > 0 ? '+' : ''}{data.momChange}%
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-tertiary text-xs font-mono">vs Last Year</span>
          <div className="flex items-center space-x-1">
            <TrendIndicator value={data.yoyChange} />
            <span className={`text-xs font-mono font-bold ${trendColor(data.yoyChange)}`}>
              {data.yoyChange > 0 ? '+' : ''}{data.yoyChange}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyRevenueWidget;
